import { useEffect, useRef, useState } from "react";
import SmsConsentNote from "../../components/SmsConsentNote";
import { useParams } from "react-router-dom";
import {
  Badge, Button, Card, Input, Layout, message, Modal, Select, Spin, Tag, Typography,
} from "antd";
import {
  Award, Bell, Calendar as CalendarIcon, Camera, Clock, Image as ImageIcon, LogOut,
  MessageSquare, Plane, Plus, Send, Trash2, Upload, User, X, ZoomIn,
} from "lucide-react";
import dayjs from "dayjs";
import axiosClient from "../../api/axiosClient";
import { ComingSoonOverlay } from "../../utils/comingSoon";

const { Content } = Layout;
const { Title, Text } = Typography;

const TOKEN_KEY = "clientAccessToken";
const CLIENT_ID_KEY = "clientId";

function formatMoney(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function resizePhoto(file: File, size = 640): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("canvas not supported"));
        const scale = Math.max(size / img.width, size / img.height);
        const w = img.width * scale, h = img.height * scale;
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── Types ────────────────────────────────────────────────────────────────────
type ClientMe = { client_id: string; name: string; email?: string; phone?: string };
type Booking = {
  booking_id: string; service: string; staff_name: string; start_time: string; end_time: string;
  status: string; price: number; headshot_photo_url?: string;
};
type ClientMessage = { message_id: string; sender: "client" | "salon"; body: string; created_at: string };
type GalleryPhoto = { photo_id: string; url: string; category: string; caption: string; created_at: string };
type NotifPrefs = {
  appt_reminders: boolean; booking_confirmations: boolean; cancellation_notices: boolean;
  special_offers: boolean; salon_updates: boolean;
};

// ── Phone login ──────────────────────────────────────────────────────────────
function ClientLoginScreen({ slug, onLoggedIn }: { slug: string; onLoggedIn: () => void }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"input" | "sending" | "sent" | "verifying">("input");

  function formatPhone(val: string) {
    const digits = val.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  async function sendCode() {
    if (phone.replace(/\D/g, "").length < 10) return;
    setStage("sending");
    try {
      await axiosClient.post("/client/auth/request-code", { slug, phone });
      setStage("sent");
    } catch {
      message.error("Something went wrong — please try again.");
      setStage("input");
    }
  }

  async function verify() {
    if (code.trim().length < 4) return;
    setStage("verifying");
    try {
      const res = await axiosClient.post("/client/auth/verify-code", { slug, phone, code: code.trim() });
      localStorage.setItem(TOKEN_KEY, res.data.access_token);
      localStorage.setItem(CLIENT_ID_KEY, res.data.client_id);
      onLoggedIn();
    } catch (err: any) {
      message.error(err.response?.data || "Invalid or expired code.");
      setStage("sent");
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(160deg,#134e4a,#0f766e 60%,#065f46)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32 }}>
        <Plane size={26} style={{ transform: "rotate(45deg)", color: "#5eead4" }} />
        <span style={{ color: "#fff", fontWeight: 700, fontSize: 22, letterSpacing: 0.5 }}>Hairplane</span>
      </div>

      <Card style={{ width: "100%", maxWidth: 380, borderRadius: 20 }}>
        {stage === "sent" || stage === "verifying" ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#ccfbf1", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <MessageSquare size={24} color="#0d9488" />
            </div>
            <Title level={4}>Enter your code</Title>
            <Text type="secondary">We sent a 6-digit code to</Text>
            <div style={{ fontWeight: 600, marginBottom: 16 }}>{phone}</div>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              size="large"
              style={{ textAlign: "center", letterSpacing: 6, fontSize: 20, marginBottom: 16 }}
            />
            <Button type="primary" block size="large" loading={stage === "verifying"} disabled={code.trim().length < 4} onClick={verify}>
              Verify & Sign In
            </Button>
            <Button type="link" onClick={() => setStage("input")} style={{ marginTop: 8 }}>
              Wrong number? Try again
            </Button>
          </div>
        ) : (
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>Sign in to your account</Title>
            <Text type="secondary">Enter your phone number — we'll text you a code. No password needed.</Text>
            <Input
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder="(555) 000-0000"
              size="large"
              style={{ marginTop: 16, marginBottom: 8 }}
            />
            <SmsConsentNote style={{ marginBottom: 16 }} />
            <Button type="primary" block size="large" icon={<Send size={15} />} loading={stage === "sending"} disabled={phone.replace(/\D/g, "").length < 10} onClick={sendCode}>
              Send Login Code
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── Main app ─────────────────────────────────────────────────────────────────
export default function ClientAppPage() {
  const { slug } = useParams<{ slug: string }>();
  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState<"bookings" | "messages" | "gallery" | "account">("bookings");
  const [me, setMe] = useState<ClientMe | null>(null);
  const [loading, setLoading] = useState(true);

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [messages, setMessages] = useState<ClientMessage[]>([]);
  const [gallery, setGallery] = useState<GalleryPhoto[]>([]);
  const [notifs, setNotifs] = useState<NotifPrefs | null>(null);
  const [loyalty, setLoyalty] = useState<{ available_points: number; tier: string; total_spend: number } | null>(null);

  const [draft, setDraft] = useState("");
  const [galleryFilter, setGalleryFilter] = useState<string>("all");
  const [lightbox, setLightbox] = useState<GalleryPhoto | null>(null);
  const [showAddPhoto, setShowAddPhoto] = useState(false);
  const [newCaption, setNewCaption] = useState("");
  const [newCategory, setNewCategory] = useState("style");
  const [reschedulingBooking, setReschedulingBooking] = useState<Booking | null>(null);
  const [addingHeadshotTo, setAddingHeadshotTo] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const headshotInputRef = useRef<HTMLInputElement>(null);

  function authHeader() {
    const token = localStorage.getItem(TOKEN_KEY);
    return { Authorization: `Bearer ${token}` };
  }

  function loadAll() {
    setLoading(true);
    Promise.all([
      axiosClient.get("/client/me", { headers: authHeader() }),
      axiosClient.get("/client/bookings", { headers: authHeader() }),
      axiosClient.get("/client/messages", { headers: authHeader() }),
      axiosClient.get("/client/gallery", { headers: authHeader() }),
      axiosClient.get("/client/notifications", { headers: authHeader() }),
      axiosClient.get("/client/loyalty", { headers: authHeader() }).catch(() => ({ data: null })),
    ])
      .then(([meRes, bRes, mRes, gRes, nRes, lRes]) => {
        setMe(meRes.data);
        setBookings(bRes.data ?? []);
        setMessages(mRes.data ?? []);
        setGallery(gRes.data ?? []);
        setNotifs(nRes.data);
        setLoyalty(lRes.data);
      })
      .catch(() => {
        handleSignOut();
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (loggedIn) loadAll();
  }, [loggedIn]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, tab]);

  function handleSignOut() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(CLIENT_ID_KEY);
    setLoggedIn(false);
    setMe(null);
  }

  async function sendMessage() {
    if (!draft.trim()) return;
    const body = draft.trim();
    setDraft("");
    try {
      await axiosClient.post("/client/messages", { body }, { headers: authHeader() });
      const res = await axiosClient.get("/client/messages", { headers: authHeader() });
      setMessages(res.data ?? []);
    } catch {
      message.error("Failed to send message.");
    }
  }

  async function cancelBooking(b: Booking) {
    Modal.confirm({
      title: "Cancel appointment?",
      content: `Cancel your ${b.service} on ${dayjs(b.start_time).format("MMM D [at] h:mm A")}?`,
      okType: "danger",
      okText: "Cancel Appointment",
      onOk: async () => {
        try {
          await axiosClient.patch(`/client/bookings/${b.booking_id}/cancel`, {}, { headers: authHeader() });
          message.success("Appointment cancelled.");
          loadAll();
        } catch (err: any) {
          message.error(err.response?.data || "Failed to cancel appointment.");
        }
      },
    });
  }

  async function submitReschedule(newStart: dayjs.Dayjs) {
    if (!reschedulingBooking) return;
    const durationMin = dayjs(reschedulingBooking.end_time).diff(dayjs(reschedulingBooking.start_time), "minute");
    try {
      await axiosClient.patch(
        `/client/bookings/${reschedulingBooking.booking_id}/reschedule`,
        { start_time: newStart.toISOString(), end_time: newStart.add(durationMin, "minute").toISOString() },
        { headers: authHeader() }
      );
      message.success("Appointment rescheduled.");
      setReschedulingBooking(null);
      loadAll();
    } catch (err: any) {
      message.error(err.response?.data || "That time is no longer available.");
    }
  }

  async function attachHeadshot(bookingID: string, file: File) {
    try {
      const url = await resizePhoto(file, 480);
      await axiosClient.post(`/client/bookings/${bookingID}/headshot`, { url }, { headers: authHeader() });
      message.success("Photo added.");
      setAddingHeadshotTo(null);
      loadAll();
    } catch {
      message.error("Failed to add photo.");
    }
  }

  async function addGalleryPhoto(file: File) {
    try {
      const url = await resizePhoto(file, 640);
      await axiosClient.post("/client/gallery", { url, category: newCategory, caption: newCaption }, { headers: authHeader() });
      setNewCaption("");
      setShowAddPhoto(false);
      const res = await axiosClient.get("/client/gallery", { headers: authHeader() });
      setGallery(res.data ?? []);
    } catch {
      message.error("Failed to add photo.");
    }
  }

  async function deleteGalleryPhoto(photoID: string) {
    try {
      await axiosClient.delete(`/client/gallery/${photoID}`, { headers: authHeader() });
      setGallery((prev) => prev.filter((p) => p.photo_id !== photoID));
      setLightbox(null);
    } catch {
      message.error("Failed to delete photo.");
    }
  }

  async function saveNotifPrefs(patch: Partial<NotifPrefs>) {
    if (!notifs) return;
    const next = { ...notifs, ...patch };
    setNotifs(next);
    try {
      await axiosClient.put("/client/notifications", next, { headers: authHeader() });
    } catch {
      message.error("Failed to save notification settings.");
    }
  }

  if (!slug) return null;
  if (!loggedIn) return <ClientLoginScreen slug={slug} onLoggedIn={() => setLoggedIn(true)} />;
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  const upcoming = bookings.filter((b) => b.status === "scheduled");
  const past = bookings.filter((b) => b.status !== "scheduled");
  const unreadCount = messages.filter((m) => m.sender === "salon").length > 0 && tab !== "messages" ? 1 : 0;
  const categories = Array.from(new Set(gallery.map((p) => p.category).filter(Boolean)));
  const filteredGallery = galleryFilter === "all" ? gallery : gallery.filter((p) => p.category === galleryFilter);

  return (
    <Layout style={{ minHeight: "100vh", background: "#f9fafb" }}>
      <div style={{ background: "#115e59", color: "#fff", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Plane size={18} style={{ transform: "rotate(45deg)", color: "#5eead4" }} />
          <span style={{ fontWeight: 700 }}>Hairplane</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Text style={{ color: "#fff", fontSize: 13 }}>{me?.name}</Text>
          <Button type="text" icon={<LogOut size={15} color="#fff" />} onClick={handleSignOut} />
        </div>
      </div>

      <Content style={{ maxWidth: 560, margin: "0 auto", width: "100%", padding: "20px 16px 90px" }}>
        {tab === "bookings" && (
          <div>
            {upcoming.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>Upcoming Appointments</Title>
                {upcoming.map((b) => (
                  <Card key={b.booking_id} style={{ marginBottom: 12, borderColor: "#99f6e4" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <div>
                        <Text strong>{b.service}</Text>
                        <div><Text type="secondary">with {b.staff_name}</Text></div>
                      </div>
                      <Tag color="teal">upcoming</Tag>
                    </div>
                    <div style={{ marginTop: 8, display: "flex", gap: 12, fontSize: 13, color: "#6b7280" }}>
                      <span><CalendarIcon size={13} style={{ marginRight: 4 }} />{dayjs(b.start_time).format("MMM D, YYYY")}</span>
                      <span><Clock size={13} style={{ marginRight: 4 }} />{dayjs(b.start_time).format("h:mm A")}</span>
                    </div>
                    <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                      <Button size="small" style={{ flex: 1 }} onClick={() => setReschedulingBooking(b)}>Reschedule</Button>
                      <Button size="small" danger style={{ flex: 1 }} onClick={() => cancelBooking(b)}>Cancel</Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            <Title level={5}>Past Appointments</Title>
            {past.length === 0 && <Text type="secondary">No past appointments yet.</Text>}
            {past.map((b) => (
              <Card key={b.booking_id} style={{ marginBottom: 12 }} bodyStyle={{ padding: 0 }}>
                <div style={{ padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                      <Text strong>{b.service}</Text>
                      <div><Text type="secondary">with {b.staff_name}</Text></div>
                    </div>
                    <Tag color={b.status === "cancelled" ? "red" : "green"}>{b.status.replace("_", " ")}</Tag>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 13, color: "#6b7280" }}>
                    {dayjs(b.start_time).format("MMM D, YYYY [at] h:mm A")} · {formatMoney(b.price)}
                  </div>
                </div>
                {b.headshot_photo_url ? (
                  <img src={b.headshot_photo_url} alt="" style={{ width: "100%", height: 160, objectFit: "cover" }} />
                ) : (
                  <div style={{ padding: "0 16px 16px" }}>
                    {addingHeadshotTo === b.booking_id ? (
                      <Button size="small" icon={<Upload size={13} />} onClick={() => headshotInputRef.current?.click()}>Choose Photo</Button>
                    ) : (
                      <Button type="link" size="small" icon={<Camera size={13} />} onClick={() => setAddingHeadshotTo(b.booking_id)} style={{ paddingLeft: 0 }}>
                        Attach headshot photo
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            ))}
            <input
              ref={headshotInputRef} type="file" accept="image/*" style={{ display: "none" }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f && addingHeadshotTo) attachHeadshot(addingHeadshotTo, f); e.target.value = ""; }}
            />
          </div>
        )}

        {tab === "messages" && (
          <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 200px)" }}>
            <div style={{ flex: 1, overflowY: "auto", marginBottom: 12 }}>
              {messages.map((m) => (
                <div key={m.message_id} style={{ display: "flex", justifyContent: m.sender === "client" ? "flex-end" : "flex-start", marginBottom: 8 }}>
                  <div style={{ maxWidth: "78%", padding: "8px 14px", borderRadius: 16, background: m.sender === "client" ? "#0d9488" : "#f3f4f6", color: m.sender === "client" ? "#fff" : "#111827" }}>
                    <div style={{ fontSize: 13 }}>{m.body}</div>
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{dayjs(m.created_at).format("MMM D, h:mm A")}</div>
                  </div>
                </div>
              ))}
              {messages.length === 0 && <Text type="secondary">No messages yet — say hello!</Text>}
              <div ref={chatEndRef} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Input
                value={draft} onChange={(e) => setDraft(e.target.value)}
                onPressEnter={sendMessage} placeholder="Message the salon…"
              />
              <Button type="primary" icon={<Send size={15} />} onClick={sendMessage} disabled={!draft.trim()} />
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              <Button size="small" type={galleryFilter === "all" ? "primary" : "default"} onClick={() => setGalleryFilter("all")}>All Photos</Button>
              {categories.map((c) => (
                <Button key={c} size="small" type={galleryFilter === c ? "primary" : "default"} onClick={() => setGalleryFilter(c)}>{c}</Button>
              ))}
              <Button size="small" type="dashed" icon={<Plus size={13} />} onClick={() => setShowAddPhoto(true)} style={{ marginLeft: "auto" }}>
                Add Photo
              </Button>
            </div>
            {filteredGallery.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af" }}>
                <ImageIcon size={40} />
                <p>Your gallery is empty. Add photos of your styles!</p>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {filteredGallery.map((p) => (
                  <div key={p.photo_id} onClick={() => setLightbox(p)} style={{ aspectRatio: "1", borderRadius: 10, overflow: "hidden", cursor: "pointer", position: "relative" }}>
                    <img src={p.url} alt={p.caption} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                ))}
              </div>
            )}
            <input ref={photoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) addGalleryPhoto(f); e.target.value = ""; }} />
          </div>
        )}

        {tab === "account" && notifs && (
          <div>
            <Card style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#0d9488", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                  <User size={22} />
                </div>
                <div>
                  <Text strong style={{ fontSize: 16 }}>{me?.name}</Text>
                  <div><Text type="secondary">{me?.phone}</Text></div>
                  {me?.email && <div><Text type="secondary">{me.email}</Text></div>}
                </div>
              </div>
            </Card>

            {loyalty && loyalty.available_points > 0 && (
              <Card style={{ marginBottom: 16, background: "linear-gradient(135deg, #0d9488, #0891b2)", border: "none" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#fff" }}>
                  <div>
                    <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>{loyalty.tier} Member</Text>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{loyalty.available_points.toLocaleString()} points</div>
                  </div>
                  <Award size={32} color="rgba(255,255,255,0.8)" />
                </div>
                <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 11 }}>Ask your stylist to redeem points for rewards next visit</Text>
              </Card>
            )}

            {/* The salon doesn't send reminders/confirmations yet, so these toggles would do nothing. */}
            <ComingSoonOverlay>
            <Card title={<span><Bell size={14} style={{ marginRight: 6 }} />Notification Preferences</span>}>
              {([
                ["appt_reminders", "Appointment Reminders", "Get notified before upcoming appointments"],
                ["booking_confirmations", "Booking Confirmations", "Confirmation when a booking is made"],
                ["cancellation_notices", "Cancellation Notices", "Alerts if an appointment is cancelled"],
                ["special_offers", "Special Offers", "Promotions and seasonal deals"],
                ["salon_updates", "Salon Updates", "News about stylists, hours, or services"],
              ] as [keyof NotifPrefs, string, string][]).map(([key, label, desc]) => (
                <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>{desc}</div>
                  </div>
                  <Button
                    size="small" type={notifs[key] ? "primary" : "default"}
                    onClick={() => saveNotifPrefs({ [key]: !notifs[key] } as Partial<NotifPrefs>)}
                  >
                    {notifs[key] ? "On" : "Off"}
                  </Button>
                </div>
              ))}
            </Card>
            </ComingSoonOverlay>

            <Button danger block style={{ marginTop: 16 }} icon={<LogOut size={14} />} onClick={handleSignOut}>
              Sign Out
            </Button>
          </div>
        )}
      </Content>

      {/* Bottom nav */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "#fff", borderTop: "1px solid #e5e7eb", display: "flex", zIndex: 20 }}>
        {[
          { id: "bookings" as const, label: "Bookings", icon: <CalendarIcon size={20} /> },
          { id: "messages" as const, label: "Messages", icon: <MessageSquare size={20} /> },
          { id: "gallery" as const, label: "Gallery", icon: <ImageIcon size={20} /> },
          { id: "account" as const, label: "Account", icon: <User size={20} /> },
        ].map((n) => (
          <button
            key={n.id}
            onClick={() => setTab(n.id)}
            style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "10px 0", border: "none", background: "transparent", cursor: "pointer", color: tab === n.id ? "#0d9488" : "#9ca3af" }}
          >
            <Badge dot={n.id === "messages" && unreadCount > 0}>{n.icon}</Badge>
            <span style={{ fontSize: 11, fontWeight: 500 }}>{n.label}</span>
          </button>
        ))}
      </div>

      {/* Reschedule modal */}
      <Modal
        title="Reschedule Appointment"
        open={!!reschedulingBooking}
        onCancel={() => setReschedulingBooking(null)}
        footer={null}
      >
        {reschedulingBooking && (
          <RescheduleForm booking={reschedulingBooking} onSubmit={submitReschedule} />
        )}
      </Modal>

      {/* Add photo modal */}
      <Modal title="Add to Gallery" open={showAddPhoto} onCancel={() => setShowAddPhoto(false)} footer={null}>
        <Select
          value={newCategory} onChange={setNewCategory} style={{ width: "100%", marginBottom: 12 }}
          options={[{ value: "style", label: "Style" }, { value: "summer", label: "Summer" }, { value: "winter", label: "Winter" }]}
        />
        <Input placeholder="Caption (optional)" value={newCaption} onChange={(e) => setNewCaption(e.target.value)} style={{ marginBottom: 12 }} />
        <Button block icon={<Upload size={14} />} onClick={() => photoInputRef.current?.click()}>Choose Photo</Button>
      </Modal>

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 50, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: 16 }} onClick={(e) => e.stopPropagation()}>
            <Button type="text" icon={<X size={18} color="#fff" />} onClick={() => setLightbox(null)} />
            <Button type="text" icon={<Trash2 size={16} color="#f87171" />} onClick={() => deleteGalleryPhoto(lightbox.photo_id)}>
              <span style={{ color: "#f87171" }}>Delete</span>
            </Button>
          </div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <img src={lightbox.url} alt={lightbox.caption} style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 12 }} onClick={(e) => e.stopPropagation()} />
          </div>
          {lightbox.caption && <div style={{ textAlign: "center", color: "#fff", paddingBottom: 24 }}>{lightbox.caption}</div>}
        </div>
      )}
    </Layout>
  );
}

function RescheduleForm({ booking, onSubmit }: { booking: Booking; onSubmit: (d: dayjs.Dayjs) => void }) {
  const [date, setDate] = useState(dayjs(booking.start_time).format("YYYY-MM-DD"));
  const [time, setTime] = useState(dayjs(booking.start_time).format("HH:mm"));
  return (
    <div>
      <Text type="secondary">{booking.service} with {booking.staff_name}</Text>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12, marginBottom: 16 }}>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
      </div>
      <Button type="primary" block onClick={() => onSubmit(dayjs(`${date}T${time}`))}>
        Save New Time
      </Button>
    </div>
  );
}
